import { useRef, useState } from "react";
import { BatteryCharging, Bike, Camera, Loader2, Trash2, Warehouse } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API_BASE } from "../lib/api";

export const VehicleCard = ({ trip, onRefresh, readOnly = false, shareToken = null }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [savedToGarage, setSavedToGarage] = useState(false);

  const saveToGarage = async () => {
    setBusy(true);
    try {
      await api.post("/garage", { vehicle: trip.vehicle, from_trip_id: trip.id, nickname: null });
      setSavedToGarage(true);
      toast.success("Bewaard in je garage — bij een nieuwe trip kies je hem in één tik!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const isMoto = trip.travel_mode === "moto" || trip.vehicle?.type === "moto";
  const riderPrefix = trip.vehicle?.rider_name ? `${trip.vehicle.rider_name} — ` : "";
  const extraVehicles = trip.extra_vehicles || [];
  const specs = isMoto
    ? `${trip.vehicle?.range_km} km per tank${trip.vehicle?.tank_liters ? ` · ${trip.vehicle.tank_liters} l` : ""}`
    : `${trip.vehicle?.range_km} km bereik · buffer 15% · laden tot 80%`;
  const photoUrl = trip.vehicle_photo_id
    ? `${API_BASE}/trips/${trip.id}/photos/${trip.vehicle_photo_id}${shareToken ? `?share=${shareToken}` : ""}`
    : null;

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("photo", file);
    try {
      await api.post(`/trips/${trip.id}/vehicle-photo`, fd);
      toast.success(`Foto van je ${isMoto ? "moto" : "auto"} opgeslagen`);
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/trips/${trip.id}/vehicle-photo`);
      toast.success("Voertuigfoto verwijderd");
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="vehicle-card" className="bg-card rounded-2xl overflow-hidden mb-6">
      {photoUrl ? (
        <div className="relative h-40 sm:h-44">
          <img data-testid="vehicle-photo" src={photoUrl} alt={`${trip.vehicle?.brand} ${trip.vehicle?.model}`} className="w-full h-full object-cover" />
          {!readOnly && (
            <div className="absolute top-2 right-2 flex gap-1.5">
              {!savedToGarage && (
                <button data-testid="vehicle-save-garage" onClick={saveToGarage} disabled={busy} title="Bewaar in mijn garage"
                  className="w-8 h-8 rounded-full bg-black/50 backdrop-blur text-white flex items-center justify-center hover:bg-black/70 transition-[background-color] duration-200">
                  <Warehouse className="w-4 h-4" />
                </button>
              )}
              <button data-testid="vehicle-photo-replace" onClick={() => inputRef.current?.click()} disabled={busy} title="Foto vervangen"
                className="w-8 h-8 rounded-full bg-black/50 backdrop-blur text-white flex items-center justify-center hover:bg-black/70 transition-[background-color] duration-200">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
              </button>
              <button data-testid="vehicle-photo-remove" onClick={remove} disabled={busy} title="Foto verwijderen"
                className="w-8 h-8 rounded-full bg-black/50 backdrop-blur text-white flex items-center justify-center hover:bg-red-600/80 transition-[background-color] duration-200">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
          <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent px-4 pt-8 pb-3">
            <p className="text-sm font-semibold text-white">{riderPrefix}{trip.vehicle?.brand} {trip.vehicle?.model}</p>
            <p className="text-xs text-white/80"><span className="font-mono">{specs}</span></p>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3 p-4">
          <span className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            {isMoto ? <Bike className="w-5 h-5 text-primary" /> : <BatteryCharging className="w-5 h-5 text-primary" />}
          </span>
          <p className="text-sm text-muted-foreground flex-1 min-w-0 basis-[11rem]">
            <span className="font-semibold text-foreground">{riderPrefix}{trip.vehicle?.brand} {trip.vehicle?.model}</span> · <span className="font-mono">{specs}</span>
          </p>
          {!readOnly && (
            <div className="shrink-0 flex gap-1.5 w-full sm:w-auto pl-[3.25rem] sm:pl-0">
              {!savedToGarage && (
                <button data-testid="vehicle-save-garage-plain" onClick={saveToGarage} disabled={busy} title="Bewaar in mijn garage"
                  className="flex items-center gap-1.5 rounded-full bg-secondary hover:bg-secondary/70 px-3.5 py-2 text-xs font-semibold transition-[background-color] duration-200">
                  <Warehouse className="w-3.5 h-3.5" /> In garage
                </button>
              )}
              <button data-testid="vehicle-photo-add" onClick={() => inputRef.current?.click()} disabled={busy}
                className="flex items-center gap-1.5 rounded-full bg-secondary hover:bg-secondary/70 px-3.5 py-2 text-xs font-semibold transition-[background-color] duration-200">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />} Foto van je {isMoto ? "moto" : "auto"}
              </button>
            </div>
          )}
        </div>
      )}
      {extraVehicles.length > 0 && (
        <div className="px-4 py-3 border-t border-border space-y-2" data-testid="extra-vehicles-list">
          {extraVehicles.map((v, i) => (
            <p key={i} className="text-sm text-muted-foreground flex items-center gap-2.5">
              <Bike className="w-4 h-4 text-primary shrink-0" />
              <span className="min-w-0 truncate">
                {v.rider_name ? <><span className="font-semibold text-foreground">{v.rider_name}</span> — </> : null}
                {v.brand} {v.model} <span className="font-mono text-xs">±{v.range_km} km per tank</span>
              </span>
            </p>
          ))}
        </div>
      )}
      {!readOnly && (
        <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid="vehicle-photo-input"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f); }} />
      )}
    </div>
  );
};
